import React, { useEffect, useState } from 'react';

import { Button } from 'shadcn/components/ui/Button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from 'shadcn/components/ui/Dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shadcn/components/ui/Table';
import { downloadCSV } from 'shared/components/csvDownload';
import dayjs from 'utils/dayjsConfig';

function PartnerCycleCountDetailsModal({ details, loading, onClose, open }) {
    const [page, setPage] = useState(0);
    const [rowsPerPage] = useState(10);

    // Reset page when modal opens or details change
    useEffect(() => {
        setPage(0);
    }, [details, open]);
    const calculateExpectedQty = (result) => {
        const caseQty = (result.expectedCaseCount || 0) * (result.unitsPerCase || 0);
        const looseQty = result.expectedLooseUnitCount || 0;
        return caseQty + looseQty;
    };

    const calculateActualQty = (result) => {
        if (result.actualCaseCount === null && result.actualLooseUnitCount === null) {
            return null;
        }
        const caseQty = (result.actualCaseCount || 0) * (result.unitsPerCase || 0);
        const looseQty = result.actualLooseUnitCount || 0;
        return caseQty + looseQty;
    };

    const calculateDifference = (result) => {
        const expected = calculateExpectedQty(result);
        const actual = calculateActualQty(result);
        if (actual === null) {
            return null;
        }
        return actual - expected;
    };

    const handleChangePage = (newPage) => {
        setPage(newPage);
    };

    const handleExportDetails = () => {
        if (!details || details.length === 0) {
            return;
        }

        const headers = [
            'SKU',
            'Product Name',
            'Partner',
            'Lot ID',
            'Pallet ID',
            'Location',
            'Expected Qty',
            'Actual Qty',
            'Difference',
            'Completed At',
            'Status',
        ];

        let csvContent = `${headers.join(',')}\r\n`;

        sortedDetails.forEach((result) => {
            const expectedQty = calculateExpectedQty(result);
            const actualQty = calculateActualQty(result);
            const difference = calculateDifference(result);
            const completedAt = result.actualResultTimestamp
                ? `${new Date(result.actualResultTimestamp).toLocaleDateString()} ${new Date(result.actualResultTimestamp).toLocaleTimeString()}`
                : '';
            const status = result.isCompleted ? 'Completed' : 'Pending';

            const rowData = [
                result.sku || '',
                `"${(result.itemName || '').replace(/"/g, '""')}"`,
                `"${(result.partnerName || '').replace(/"/g, '""')}"`,
                result.lotID || 'N/A',
                result.palletID || '',
                result.locationName || '',
                expectedQty,
                actualQty !== null ? actualQty : '',
                difference !== null ? difference : '',
                `"${completedAt}"`,
                `"${status}"`,
            ];

            csvContent += `${rowData.join(',')}\r\n`;
        });

        const timestamp = dayjs().format('YYYY_MM_DD_HH_mm');
        const filename = `partner_cycle_count_details_${timestamp}.csv`;
        downloadCSV(csvContent, filename);
    };

    // Sort details so completed results are at the top
    const sortedDetails = details
        ? [...details].sort((a, b) => {
              if (a.isCompleted === b.isCompleted) return 0;
              return a.isCompleted ? -1 : 1;
          })
        : [];

    const paginatedDetails = sortedDetails.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    const totalPages = sortedDetails.length > 0 ? Math.ceil(sortedDetails.length / rowsPerPage) : 0;

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="!max-w-[95vw] max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                    <div className="flex justify-between items-center">
                        <DialogTitle>Cycle Count Details</DialogTitle>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleExportDetails}
                            disabled={!details || details.length === 0}
                        >
                            Export CSV
                        </Button>
                    </div>
                </DialogHeader>
                {loading ? (
                    <div className="flex items-center justify-center py-8">
                        <div className="text-muted-foreground">Loading details...</div>
                    </div>
                ) : details ? (
                    <div className="space-y-4">
                        <div className="rounded-md border">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>SKU</TableHead>
                                        <TableHead>Product Name</TableHead>
                                        <TableHead>Partner</TableHead>
                                        <TableHead>Lot ID</TableHead>
                                        <TableHead>Pallet ID</TableHead>
                                        <TableHead>Location</TableHead>
                                        <TableHead>Expected Qty</TableHead>
                                        <TableHead>Actual Qty</TableHead>
                                        <TableHead>Difference</TableHead>
                                        <TableHead>Completed At</TableHead>
                                        <TableHead>Status</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {paginatedDetails && paginatedDetails.length > 0 ? (
                                        paginatedDetails.map((result, index) => {
                                            const expectedQty = calculateExpectedQty(result);
                                            const actualQty = calculateActualQty(result);
                                            const difference = calculateDifference(result);

                                            return (
                                                <TableRow key={result.expectedResultID || index}>
                                                    <TableCell>{result.sku}</TableCell>
                                                    <TableCell>{result.itemName}</TableCell>
                                                    <TableCell>{result.partnerName || '-'}</TableCell>
                                                    <TableCell>{result.lotID || 'N/A'}</TableCell>
                                                    <TableCell>{result.palletID || '-'}</TableCell>
                                                    <TableCell>{result.locationName || '-'}</TableCell>
                                                    <TableCell>{expectedQty}</TableCell>
                                                    <TableCell>{actualQty !== null ? actualQty : '-'}</TableCell>
                                                    <TableCell
                                                        className={
                                                            difference !== null && difference !== 0
                                                                ? 'text-destructive font-medium'
                                                                : ''
                                                        }
                                                    >
                                                        {difference !== null ? difference : '-'}
                                                    </TableCell>
                                                    <TableCell>
                                                        {result.actualResultTimestamp ? (
                                                            <div className="flex flex-col">
                                                                <span>
                                                                    {new Date(
                                                                        result.actualResultTimestamp,
                                                                    ).toLocaleDateString()}
                                                                </span>
                                                                <span className="text-xs text-muted-foreground">
                                                                    {new Date(
                                                                        result.actualResultTimestamp,
                                                                    ).toLocaleTimeString()}
                                                                </span>
                                                            </div>
                                                        ) : (
                                                            '-'
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {result.isCompleted ? (
                                                            <span className="text-green-600">Completed</span>
                                                        ) : (
                                                            <span className="text-muted-foreground">Pending</span>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={11} className="h-24 text-center">
                                                No detailed results available.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                        {sortedDetails && sortedDetails.length > 0 && (
                            <div className="flex items-center justify-between py-4">
                                <div className="text-sm text-muted-foreground">
                                    Showing {page * rowsPerPage + 1}-
                                    {Math.min((page + 1) * rowsPerPage, sortedDetails.length)} of {sortedDetails.length}{' '}
                                    results
                                </div>
                                <div className="flex items-center space-x-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleChangePage(page - 1)}
                                        disabled={page === 0}
                                    >
                                        Previous
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleChangePage(page + 1)}
                                        disabled={page >= totalPages - 1}
                                    >
                                        Next
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                ) : null}
            </DialogContent>
        </Dialog>
    );
}

export { PartnerCycleCountDetailsModal };
